import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { AudioPlayerPill } from './ui';
export interface Character {
  id: string;
  name: string;
  hometown: string;
  color: string;
  borderColorClass: string;
  quote: string;
  tagline?: string;
  bio?: string;
  catchphrases?: string[];
  isInterrupt?: boolean;
  avatarUrl?: string;
}
interface CharacterCardProps {
  character: Character;
  expanded?: boolean;
}
// Map character IDs to curated Unsplash portrait URLs
const AVATAR_URLS: Record<string, string> = {
  vinny: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  dale: 'https://images.unsplash.com/photo-1583195764036-6dc248ac07d9?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  marisol: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  giuseppe: 'https://images.unsplash.com/photo-1566753323558-f4e0952af115?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  daniel: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  gary: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  kevin: 'https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  marcus: 'https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  ricky: 'https://images.unsplash.com/photo-1621252179027-94459d278660?w=300&h=300&fit=crop&crop=face&auto=format&q=80',
  jorge: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=300&h=300&fit=crop&crop=face&auto=format&q=80'
};
// Color overlay per character for the duotone treatment
const OVERLAY_COLORS: Record<string, string> = {
  vinny: 'from-blue-900/60 to-blue-500/20',
  dale: 'from-green-900/60 to-green-600/20',
  marisol: 'from-rose-900/60 to-rose-500/20',
  giuseppe: 'from-red-950/60 to-red-800/20',
  daniel: 'from-slate-800/60 to-slate-400/20',
  gary: 'from-orange-900/60 to-orange-500/20',
  kevin: 'from-red-900/60 to-red-600/20',
  marcus: 'from-fuchsia-900/60 to-fuchsia-500/20',
  ricky: 'from-yellow-900/60 to-yellow-400/20',
  jorge: 'from-red-800/60 to-red-500/20',
  commentator: 'from-charcoal/90 to-charcoal/90'
};
export const CharacterCard = ({
  character,
  expanded = false
}: CharacterCardProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [imgError, setImgError] = useState(false);
  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      setTimeout(() => setIsPlaying(false), 3000);
    }
  };
  const avatarUrl = character.avatarUrl || AVATAR_URLS[character.id];
  const overlayColor = OVERLAY_COLORS[character.id] || 'from-charcoal/60 to-charcoal/20';
  const isCommentator = character.id === 'commentator';
  return <motion.div whileHover={{
    y: -5
  }} className={`relative bg-dark rounded-2xl p-6 border-2 transition-all duration-300 flex flex-col h-full group ${character.borderColorClass} hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]`} data-id="element-292">
      {/* Avatar */}
      <div className={`w-28 h-28 rounded-full mx-auto mb-6 overflow-hidden relative border-4 shadow-xl ${character.borderColorClass}`} data-id="element-293">
        {isCommentator ?
      // Silhouette treatment for The Commentator
      <div className="w-full h-full bg-charcoal flex items-center justify-center relative" data-id="element-294">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(200,255,0,0.15)_0%,_transparent_70%)]" data-id="element-295"></div>
            <span className="text-5xl font-black font-heading text-lime/40" data-id="element-296">
              ?
            </span>
          </div> : avatarUrl && !imgError ? <>
            <img src={avatarUrl} alt={character.name} className="w-full h-full object-cover" onError={() => setImgError(true)} loading="lazy" data-id="element-297" />
            {/* Duotone color overlay */}
            <div className={`absolute inset-0 bg-gradient-to-br ${overlayColor} mix-blend-multiply`} data-id="element-298"></div>
            {/* Vignette edge */}
            <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(0,0,0,0.5)]" data-id="element-299"></div>
          </> :
      // Fallback: styled initial
      <div className={`w-full h-full flex items-center justify-center ${character.borderColorClass.replace('border-', 'bg-')}`} style={{
        opacity: 0.3
      }} data-id="element-300">
            <span className="text-5xl font-black font-heading text-white drop-shadow-lg" style={{
          opacity: 1
        }} data-id="element-301">
              {character.name.charAt(0)}
            </span>
          </div>}
      </div>

      {/* Header */}
      <div className="text-center mb-6" data-id="element-302">
        <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-1" data-id="element-303">
          {character.name}
        </h3>
        <p className="text-xs font-mono text-slate uppercase tracking-wider mb-2" data-id="element-304">
          {character.hometown}
        </p>
        {character.tagline && <p className="text-sm font-medium text-white/80 italic" data-id="element-305">
            "{character.tagline}"
          </p>}
      </div>

      {/* Quote Bubble */}
      <div className="relative bg-charcoal p-4 rounded-xl rounded-tl-none mb-6 flex-1 border border-white/5" data-id="element-306">
        <div className="absolute -top-3 -left-0 w-4 h-4 bg-charcoal border-t border-l border-white/5 transform -skew-x-12" data-id="element-307"></div>
        <p className={`text-lg font-medium italic ${character.isInterrupt ? 'text-crimson font-bold' : 'text-white'}`} data-id="element-308">
          "{character.quote}"
        </p>
      </div>

      {/* Expanded Content */}
      {expanded && character.bio && <div className="mb-6 flex-1" data-id="element-309">
          <p className="text-sm text-slate mb-4" data-id="element-310">{character.bio}</p>
          {character.catchphrases && <div className="space-y-2" data-id="element-311">
              <p className="text-xs font-bold text-white uppercase tracking-wider" data-id="element-312">
                Signature Calls:
              </p>
              <ul className="text-sm text-slate space-y-1" data-id="element-313">
                {character.catchphrases.map((phrase, i) => <li key={i} className="flex gap-2" data-id="element-314">
                    <span className="text-lime" data-id="element-315">›</span> {phrase}
                  </li>)}
              </ul>
            </div>}
        </div>}

      {/* Footer Actions */}
      <div className="mt-auto flex flex-col gap-3" data-id="element-316">
        <AudioPlayerPill isPlaying={isPlaying} onClick={togglePlay} characterName="Play Voice" data-id="element-317" />

        {expanded && <button className="w-full py-3 rounded-lg font-bold uppercase tracking-wider text-sm border border-white/20 hover:bg-white hover:text-charcoal transition-colors" data-id="element-318">
            Add to My Cast
          </button>}
      </div>
    </motion.div>;
};